import React, { useEffect, useRef, useState } from 'react'
import ProtectedRoute from '../../routes/protected'
import SidebarCargosSalarios from '../../components/sidebars/sidebarCargosSalarios'
import salaryAnalysisService from '../../services/data/salary-analysis.service'
import companyService from '../../services/data/company.service'
import currencyFormat from '../../helpers/currencyFormat'
import useCompanyData from '../../context/company'
import UsePerms from '../../context/userAccess'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Dropdown } from 'primereact/dropdown'
import { Toast } from 'primereact/toast'

function bandHint(amount, minV, midV, maxV) {
    const a = Number(amount)
    if (!a || minV === null || maxV === null) return null
    if (a < minV) return { label: 'Abaixo da faixa mínima', cls: 'warning' }
    if (a > maxV) return { label: 'Acima da faixa máxima', cls: 'danger' }
    return { label: 'Dentro da faixa da posição', cls: 'success' }
}

const STATUS_BADGE = {
    dentro: <span className='badge bg-success'>Dentro</span>,
    acima: <span className='badge bg-danger'>Acima</span>,
    abaixo: <span className='badge bg-warning text-dark'>Abaixo</span>,
    sem_estrutura: <span className='badge bg-secondary'>Sem estrutura</span>,
    sem_dados: <span className='badge bg-light text-muted border'>Sem dados</span>,
}

const STATUS_OPTIONS = [
    { label: 'Todos', value: '' },
    { label: 'Dentro da faixa', value: 'dentro' },
    { label: 'Acima da faixa', value: 'acima' },
    { label: 'Abaixo da faixa', value: 'abaixo' },
    { label: 'Sem estrutura', value: 'sem_estrutura' },
    { label: 'Sem dados', value: 'sem_dados' },
]

function RangeBar({ pct, status }) {
    if (pct === null || pct === undefined) return <span className='text-muted small'>—</span>
    const clamped = Math.max(0, Math.min(100, pct))
    const color = status === 'acima' ? 'bg-danger' : status === 'abaixo' ? 'bg-warning' : 'bg-success'
    return (
        <div className='d-inline-flex align-items-center gap-2' style={{ minWidth: 120 }}>
            <div className='progress flex-grow-1' style={{ height: 8 }}>
                <div className={`progress-bar ${color}`} style={{ width: `${clamped}%` }} />
            </div>
            <span className='small text-muted' style={{ minWidth: 36 }}>{clamped}%</span>
        </div>
    )
}

export default function SalariosColaboradores() {
    const toast = useRef(null)
    const { rootUser } = useCompanyData()
    const VerifyPerms = UsePerms()
    const canManageOthers = VerifyPerms(['change_jobposition']) || rootUser

    const [rows, setRows] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    const [modalOpen, setModalOpen] = useState(false)
    const [activeRow, setActiveRow] = useState(null)
    const [amount, setAmount] = useState('')
    const [effectiveDate, setEffectiveDate] = useState('')
    const [reason, setReason] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [subcompanySel, setSubcompanySel] = useState(null)
    const [departmentSel, setDepartmentSel] = useState(null)
    const [costcenterSel, setCostcenterSel] = useState(null)
    const [subcompanyItems, setSubcompanyItems] = useState([])
    const [departmentItems, setDepartmentItems] = useState([])
    const [costcenterItems, setCostcenterItems] = useState([])

    function buildFilters() {
        const f = {}
        if (search.trim()) f.search = search.trim()
        if (statusFilter) f.salary_status = statusFilter
        if (subcompanySel) f.subcompany_id = subcompanySel.id
        if (departmentSel) f.department_id = departmentSel.id
        if (costcenterSel) f.costcenter_id = costcenterSel.id
        return f
    }

    function loadRows(p = 1, filters = {}) {
        setLoading(true)
        setError('')
        salaryAnalysisService.listEmployeesWithSalary(p, filters)
            .then(r => {
                setRows(r.results || [])
                setCount(r.count || 0)
            })
            .catch(e => setError(JSON.stringify(e?.response?.data || e)))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        companyService.autoCompleteSubCompany('')
            .then(r => setSubcompanyItems(r || []))
            .catch(() => setSubcompanyItems([]))
        companyService.autoCompleteDepartment('')
            .then(r => setDepartmentItems(r || []))
            .catch(() => setDepartmentItems([]))
        companyService.autoCompleteCostCenter('')
            .then(r => setCostcenterItems(r || []))
            .catch(() => setCostcenterItems([]))
    }, [])

    useEffect(() => {
        loadRows(page, buildFilters())
    }, [page])

    function applyFilters() {
        if (page === 1) loadRows(1, buildFilters())
        else setPage(1)
    }

    function clearFilters() {
        setSearch('')
        setStatusFilter('')
        setSubcompanySel(null)
        setDepartmentSel(null)
        setCostcenterSel(null)
        if (page === 1) loadRows(1, {})
        else setPage(1)
    }

    function openModal(row) {
        setActiveRow(row)
        setAmount('')
        setReason('')
        setEffectiveDate(new Date().toISOString().slice(0, 10))
        setModalOpen(true)
    }

    function closeModal() {
        setModalOpen(false)
        setActiveRow(null)
    }

    function submitSalary(e) {
        e.preventDefault()
        if (!activeRow) return
        const val = String(amount).replace(',', '.').trim()
        const num = parseFloat(val)
        if (!num || num <= 0) {
            toast.current?.show({ severity: 'warn', summary: 'Valor inválido', detail: 'Informe um salário maior que zero.', life: 4000 })
            return
        }
        if (!effectiveDate) {
            toast.current?.show({ severity: 'warn', summary: 'Data', detail: 'Informe a data de vigência.', life: 4000 })
            return
        }
        setSubmitting(true)
        const payload = {
            employee: activeRow.employee_id,
            amount: num,
            effective_date: effectiveDate,
            currency: 'BRL',
            reason: reason || (canManageOthers ? 'Registro / enquadramento' : 'Autodeclaração de salário'),
        }
        if (activeRow.primary_jobposition_id) {
            payload.jobposition = activeRow.primary_jobposition_id
        }
        salaryAnalysisService.createEmployeeSalary(payload)
            .then(() => {
                toast.current?.show({ severity: 'success', summary: 'Salário registrado', detail: 'O registro foi salvo com sucesso.', life: 4000 })
                closeModal()
                loadRows(page, buildFilters())
            })
            .catch(err => {
                const msg = err?.response?.data ? JSON.stringify(err.response.data) : String(err)
                toast.current?.show({ severity: 'error', summary: 'Erro', detail: msg, life: 6000 })
            })
            .finally(() => setSubmitting(false))
    }

    const hint = activeRow ? bandHint(amount, activeRow.min_salary, activeRow.mid_salary, activeRow.max_salary) : null

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid">
                <div className='row flex-nowrap'>
                    <SidebarCargosSalarios />
                    <div className="col-xs-10 col-sm-9 col-md-9 col-xl-10 p-0 formMarginBottom">
                        <div className='col text-bg-green p-3'>
                            <span className="fs-6 fw-bold">Salários dos Colaboradores</span>
                        </div>

                        <div className="alert alert-light border mx-3 mt-3 mb-0 small" role="note">
                            <FontAwesomeIcon icon={solid('lock')} className="me-2 text-muted" />
                            <strong>Privacidade:</strong> valores salariais são dados sensíveis.
                            {!canManageOthers && (
                                <span> Você visualiza apenas o seu enquadramento salarial.</span>
                            )}
                            {canManageOthers && (
                                <span> A listagem completa requer permissão de gestão de cargos ou perfil de dono da empresa.</span>
                            )}
                        </div>

                        <div className='row m-3 g-2 align-items-end'>
                            <div className='col-md-3'>
                                <label className='form-label small fw-bold'>Nome</label>
                                <input
                                    className='form-control form-control-sm'
                                    placeholder='Buscar colaborador...'
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && applyFilters()}
                                />
                            </div>
                            {canManageOthers && (
                                <>
                                    <div className='col-md-2'>
                                        <label className='form-label small fw-bold'>Organização</label>
                                        <Dropdown className='w-100' placeholder='Organização'
                                            value={subcompanySel}
                                            options={subcompanyItems}
                                            optionLabel='title'
                                            filter
                                            showClear
                                            onChange={e => setSubcompanySel(e.value)} />
                                    </div>
                                    <div className='col-md-2'>
                                        <label className='form-label small fw-bold'>Departamento</label>
                                        <Dropdown className='w-100' placeholder='Departamento'
                                            value={departmentSel}
                                            options={departmentItems}
                                            optionLabel='title'
                                            filter
                                            showClear
                                            onChange={e => setDepartmentSel(e.value)} />
                                    </div>
                                    <div className='col-md-2'>
                                        <label className='form-label small fw-bold'>Centro de custo</label>
                                        <Dropdown className='w-100' placeholder='Centro de custo'
                                            value={costcenterSel}
                                            options={costcenterItems}
                                            optionLabel='title'
                                            filter
                                            showClear
                                            onChange={e => setCostcenterSel(e.value)} />
                                    </div>
                                </>
                            )}
                            <div className='col-md-2'>
                                <label className='form-label small fw-bold'>Status vs. faixa</label>
                                <Dropdown className='w-100' placeholder='Status'
                                    value={statusFilter}
                                    options={STATUS_OPTIONS}
                                    optionLabel='label'
                                    optionValue='value'
                                    onChange={e => setStatusFilter(e.value)} />
                            </div>
                            <div className='col-auto'>
                                <button type='button' className='btn btn-success btn-sm' onClick={applyFilters}>
                                    <FontAwesomeIcon icon={solid('magnifying-glass')} /> Filtrar
                                </button>
                            </div>
                            <div className='col-auto'>
                                <button type='button' className='btn btn-link btn-sm text-grey text-decoration-none' onClick={clearFilters}>
                                    Limpar
                                </button>
                            </div>
                        </div>

                        {error && <div className='alert alert-danger mx-3'>{error}</div>}

                        {loading && (
                            <div className='text-center p-5'>
                                <div className="spinner-border text-success" role="status" />
                            </div>
                        )}

                        {!loading && (
                            <div className='m-3'>
                                <div className='card border-clean'>
                                    <div className='card-body p-0'>
                                        <div className='p-3 border-bottom small text-muted'>
                                            {count === 0
                                                ? 'Nenhum colaborador alocado em posição neste filtro.'
                                                : `${count} colaborador(es) alocado(s) em posição.`}
                                        </div>
                                        <div className='table-responsive'>
                                            <table className='table table-hover table-sm mb-0'>
                                                <thead className='table-light'>
                                                    <tr>
                                                        <th>Colaborador</th>
                                                        <th>Posição</th>
                                                        {canManageOthers && <th>Organização / Depto / CC</th>}
                                                        <th className='text-end'>Salário</th>
                                                        <th className='text-end'>Faixa (mín / mid / máx)</th>
                                                        <th className='text-center'>% Mid</th>
                                                        <th className='text-center'>Desvio</th>
                                                        <th className='text-center'>Posição na faixa</th>
                                                        <th className='text-center'>Status</th>
                                                        <th className='text-center'>Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map(row => (
                                                        <tr key={row.employee_id}>
                                                            <td className='align-middle fw-semibold'>{row.employee_name}</td>
                                                            <td className='align-middle small'>
                                                                {(row.position_titles || []).join(' · ') || row.position_title || '—'}
                                                            </td>
                                                            {canManageOthers && (
                                                                <td className='align-middle small text-muted'>
                                                                    {[row.subcompany_title, row.department_title, row.costcenter_title]
                                                                        .filter(Boolean)
                                                                        .join(' · ') || '—'}
                                                                </td>
                                                            )}
                                                            <td className='align-middle text-end fw-bold'>
                                                                {row.salary_amount !== null ? currencyFormat(row.salary_amount) : '—'}
                                                            </td>
                                                            <td className='align-middle text-end small'>
                                                                {row.min_salary !== null
                                                                    ? `${currencyFormat(row.min_salary)} / ${currencyFormat(row.mid_salary)} / ${currencyFormat(row.max_salary)}`
                                                                    : '—'}
                                                            </td>
                                                            <td className='align-middle text-center'>
                                                                {row.pct_of_mid !== null ? (
                                                                    <span className={`fw-bold ${row.pct_of_mid > 100 ? 'text-danger' : row.pct_of_mid < 100 ? 'text-warning' : 'text-success'}`}>
                                                                        {row.pct_of_mid}%
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td className='align-middle text-center'>
                                                                {row.deviation_pct !== null ? (
                                                                    <span className={`fw-bold ${row.deviation_pct > 0 ? 'text-danger' : row.deviation_pct < 0 ? 'text-warning' : 'text-success'}`}>
                                                                        {row.deviation_pct > 0 ? '+' : ''}{row.deviation_pct}%
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td className='align-middle text-center'>
                                                                <RangeBar pct={row.range_position_pct} status={row.salary_status} />
                                                            </td>
                                                            <td className='align-middle text-center'>
                                                                {STATUS_BADGE[row.salary_status] || '—'}
                                                            </td>
                                                            <td className='align-middle text-center'>
                                                                {!row.has_salary && (
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-success btn-sm btn-rounded'
                                                                        style={{fontSize: '0.775rem', padding: '0.375rem 0.75rem'}}
                                                                        onClick={() => openModal(row)}
                                                                    >
                                                                        <FontAwesomeIcon icon={solid('plus')} className='me-1' />
                                                                        Adicionar salário
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {rows.length > 0 && (
                                            <div className='p-2 border-top d-flex justify-content-between align-items-center flex-wrap gap-2'>
                                                <span className='small text-muted'>
                                                    Página {page} · {count} registro(s)
                                                </span>
                                                <nav>
                                                    <ul className='pagination pagination-sm mb-0'>
                                                        <li className='page-item'>
                                                            <button
                                                                type='button'
                                                                className='page-link'
                                                                disabled={page === 1 || loading}
                                                                onClick={() => setPage(page - 1)}
                                                            >
                                                                <FontAwesomeIcon icon={solid('angle-left')} />
                                                            </button>
                                                        </li>
                                                        <li className='page-item'>
                                                            <span className='page-link text-muted'>{page}</span>
                                                        </li>
                                                        <li className='page-item'>
                                                            <button
                                                                type='button'
                                                                className='page-link'
                                                                disabled={rows.length < 50 || loading}
                                                                onClick={() => setPage(page + 1)}
                                                            >
                                                                <FontAwesomeIcon icon={solid('angle-right')} />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {modalOpen && activeRow && (
                <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.35)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-clean">
                            <div className="modal-header">
                                <h6 className="modal-title fw-bold">Registrar salário — {activeRow.employee_name}</h6>
                                <button type="button" className="btn-close" onClick={closeModal} aria-label="Fechar" />
                            </div>
                            <form onSubmit={submitSalary}>
                                <div className="modal-body">
                                    <p className="small text-muted">
                                        Faixa da posição (referência):{' '}
                                        {activeRow.min_salary !== null
                                            ? `${currencyFormat(activeRow.min_salary)} — ${currencyFormat(activeRow.mid_salary)} — ${currencyFormat(activeRow.max_salary)}`
                                            : 'Sem estrutura salarial vinculada à posição.'}
                                    </p>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Salário (R$)</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            value={amount}
                                            onChange={e => setAmount(e.target.value)}
                                            placeholder="Ex.: 7500,00"
                                        />
                                        {hint && (
                                            <div className={`mt-1 small text-${hint.cls}`}>
                                                <FontAwesomeIcon icon={solid('circle-info')} className="me-1" />
                                                {hint.label}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Data de vigência</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={effectiveDate}
                                            onChange={e => setEffectiveDate(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-0">
                                        <label className="form-label small fw-bold">Motivo / observação (opcional)</label>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows={2}
                                            value={reason}
                                            onChange={e => setReason(e.target.value)}
                                            placeholder="Ex.: Promoção, reajuste anual, enquadramento..."
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-success btn-sm" disabled={submitting}>
                                        {submitting ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </ProtectedRoute>
    )
}
